import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import graphqlClientFactory from 'lib/graphql-client-factory';
import {
  FundResolverDocument,
  FundResolverQueryVariables,
} from 'components/graphql/FundResolver.graphql';
import {
  DailyPricingRequest,
  FundCentreOverviewDataBridgeResponse,
  FundCentrePricesDataBridgeResponse,
  FundCentreTabRequest,
} from 'model/ApiGateway';
import { apiHost } from 'api/apiHost';
import { requestHandler } from '@/store/handleRequestLanguage';

const graphQLClient = graphqlClientFactory();

export type FundCentreTabDataRequestParams = FundCentreTabRequest & { tab: 'overview' | 'prices' };

export type DailyPricingResponse = {
  sharePrice?: number;
  dividendYield?: number;
  dateTime?: string;
  currency: string | null;
  [k: string]: string | number | null | undefined;
};

const customBaseQuery = fetchBaseQuery({
  baseUrl: `${apiHost}/api`,
});

const baseQueryHandler = async (args, api) => requestHandler(customBaseQuery, args, api);

// Define a service using a base URL and expected endpoints
export const fundCentreApi = createApi({
  reducerPath: 'fundCentreApi',
  tagTypes: ['Funds'],
  baseQuery: baseQueryHandler,
  endpoints: (build) => ({
    getFundCentreTabData: build.query<
      FundCentreOverviewDataBridgeResponse | FundCentrePricesDataBridgeResponse,
      FundCentreTabDataRequestParams
    >({
      queryFn: async (_arg, _queryApi, _extraOptions, fetchWithBQ) => {
        const selectedTab = _arg.tab;
        const { data } = await fetchWithBQ({
          url: `/funds/${selectedTab}`,
          method: 'POST',
          body: _arg,
        });

        const configurableFundsIDs = data.content[selectedTab]
          ?.filter((fund) => fund.hasConfigurableLink)
          .map((fund) => {
            return { name: 'databridgeFundId', value: fund.id, operator: 'EQ' };
          });

        if (!configurableFundsIDs.length) return { data };

        const result = await graphQLClient.request<FundResolverQueryVariables>(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          FundResolverDocument as any,
          {
            language: _arg.language,
            fundIds: configurableFundsIDs,
            fundsLength: configurableFundsIDs.length,
          }
        );

        const mappedFundData = data.content[selectedTab]?.map((fund) => {
          const configurableFund = result.search.results.find(
            (cf) => cf.databridgeFundName?.value === fund.name && fund.hasConfigurableLink
          );

          if (configurableFund) {
            return {
              ...fund,
              shareclasses: fund.shareclasses.map((shareClass) => {
                const defaultUrl = `/${fund.name.toLowerCase()}-${configurableFund.children.results[0]?.field?.value.toLowerCase()}`;
                return {
                  ...shareClass,
                  url: configurableFund.investmentTrustLink?.url || defaultUrl,
                };
              }),
            };
          }

          return { ...fund }; // fallback if no match
        });

        return {
          data: {
            ...data,
            content: {
              ...data.content,
              [selectedTab]: mappedFundData,
            },
          },
        };
      },
    }),
    getFundsFacets: build.query<any>({
      query: (body) => ({
        url: '/funds/fundfacets',
        method: 'POST',
        body: body,
      }),
    }),
    fetchSearchResults: build.query<any>({
      query: (body) => ({
        url: '/funds/search',
        method: 'POST',
        body: body,
      }),
    }),
    fetchDailyPricing: build.query<DailyPricingResponse, DailyPricingRequest>({
      query: (body) => ({
        url: '/funds/dailypricing',
        method: 'POST',
        body: body,
      }),
    }),
    fetchAuthorisedFunds: build.query<any>({
      query: (body) => ({
        url: '/funds/authorisedFunds',
        method: 'POST',
        body: body,
      }),
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useLazyGetFundCentreTabDataQuery,
  useLazyGetFundsFacetsQuery,
  useLazyFetchSearchResultsQuery,
  useFetchDailyPricingQuery,
  useLazyFetchAuthorisedFundsQuery,
} = fundCentreApi;
